import { useMemo, useState } from "react";
import { OrderSerial } from "models/OrderSerial";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import ProductsTable, { ActionHandler } from "./ProductsTable";
import EditOrderSerialDialog from "dialogs/EditOrderSerialDialog";
import { Link } from "react-router-dom";
import Section from "components/Section";
import { useAuthContext } from "features/auth/AuthContext";
import { UserRole } from "features/auth/types/user";
import EditSerialEntityDialog from "dialogs/EditSerialEntityDialog";
import AddSerialEntityDialog from "dialogs/AddSerialEntityDialog";
import {
  orderSerialToProduct,
  productToSerialEntity,
  serialEntityToProduct,
} from "utilities/serialEntityToOrderSerial";
import { Product } from "models/Product";
import { useChecklistContext } from "context/ChecklistContext";
import CreateChildSerialDialog from "../../../../dialogs/CreateChildSerialDialog";
import MoveProductDialog from "../../../../dialogs/MoveProductDialog";
import { useOrderContext } from "context/OrderContext";
import DeleteProductDialog from "dialogs/DeleteProductDialog";
import UninstallProductDialog from "dialogs/UninstallProductDialog";
import EmptyMessage from "components/EmptyMessage";

export type OrderSerialWithChildren = OrderSerial & { children: OrderSerial[] };

const ChecklistProductsSection: React.FC = () => {
  const { hasRole } = useAuthContext();
  const { checklist } = useChecklistContext();
  const order = useOrderContext();
  const [searchString, setSearchString] = useState<string>("");
  const [hideExisting, setHideExisting] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showAddSerialEntity, setShowAddSerialEntity] = useState(false);
  const [showAddChildSerial, setShowAddChildSerial] = useState(false);
  const [showEditProduct, setShowEditProduct] = useState(false);
  const [showDeleteProduct, setShowDeleteProduct] = useState(false);
  const [showMoveProduct, setShowMoveProduct] = useState(false);
  const [showUninstallProduct, setShowUninstallProduct] = useState(false);

  const userCanEditProducts = hasRole([
    UserRole.ADMIN,
    UserRole.USER_ADMIN,
    UserRole.SALES,
    UserRole.INTERNAL_INSTALLER,
    UserRole.EXTERNAL_INSTALLER,
  ]);

  // Combine OrderSerials and SerialEntities into one list of Products
  const allProducts = useMemo(() => {
    const serials = checklist.installedProducts.map((os) => orderSerialToProduct(os));
    const serialEntities = checklist.serialEntities.map((se) =>
      serialEntityToProduct(se, checklist)
    );
    return [...serials, ...serialEntities];
  }, [checklist]);

  // Filter products based on search string
  const filteredProducts = useMemo(() => {
    const matchesSearch = (s: Product) =>
      s.serial?.toLowerCase().includes(searchString.toLowerCase()) ||
      s.item?.toLowerCase().includes(searchString.toLowerCase()) ||
      s.description?.toLowerCase().includes(searchString.toLowerCase());

    return (
      allProducts
        .filter((s) => !hideExisting || !s.existing)
        .filter((s) => matchesSearch(s) || s.children.some(matchesSearch)) ?? []
    );
  }, [allProducts, searchString, hideExisting]);

  const handleAction: ActionHandler = (product, action) => {
    setSelectedProduct(product);
    switch (action) {
      case "edit":
        setShowEditProduct(true);
        break;
      case "add-child":
        setShowAddChildSerial(true);
        break;
      case "delete":
        setShowDeleteProduct(true);
        break;
      case "move":
        setShowMoveProduct(true);
        break;
      case "uninstall":
        setShowUninstallProduct(true);
        break;
      default:
        console.warn(`Unknown ProductsTable action: ${action}`);
        break;
    }
  };

  return (
    <Section p={1}>
      {allProducts.length > 0 && (
        <Box sx={{ mb: 1, display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            value={searchString}
            onChange={(e) => setSearchString(e.target.value)}
            sx={{ width: "100%", maxWidth: "30ch" }}
            variant="filled"
            label="Sök produkt"
            type="search"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              },
            }}
          />

          <FormControlLabel
            control={
              <Checkbox checked={hideExisting} onChange={() => setHideExisting(!hideExisting)} />
            }
            label="Dölj befintliga"
            labelPlacement="end"
          />
        </Box>
      )}

      {allProducts.length === 0 ? (
        <EmptyMessage m={1}>Inga produkter registrerade</EmptyMessage>
      ) : (
        <ProductsTable serials={filteredProducts} onAction={handleAction} />
      )}

      {userCanEditProducts && (
        <Box display="flex" justifyContent="flex-end" gap={1} sx={{ mt: 2 }}>
          <Tooltip title="Registrera befintlig produkt, inte kopplad till ordern" arrow>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setShowAddSerialEntity(true)}
            >
              Bef. produkt
            </Button>
          </Tooltip>

          <Tooltip title="Lägg till serienummer som registrerats på ordern" arrow>
            <Button component={Link} to="products" variant="contained" startIcon={<AddIcon />}>
              Ny produkt
            </Button>
          </Tooltip>
        </Box>
      )}

      {showAddSerialEntity && (
        <AddSerialEntityDialog
          checklistId={checklist.id}
          onClose={() => setShowAddSerialEntity(false)}
        />
      )}

      {showAddChildSerial && selectedProduct && (
        <CreateChildSerialDialog
          parent={selectedProduct}
          onClose={() => setShowAddChildSerial(false)}
        />
      )}

      {showEditProduct && selectedProduct && !selectedProduct.existing && (
        <EditOrderSerialDialog
          serial={selectedProduct as OrderSerial}
          onClose={() => {
            setSelectedProduct(null);
            setShowEditProduct(false);
          }}
        />
      )}

      {showEditProduct && selectedProduct && selectedProduct.existing && (
        <EditSerialEntityDialog
          serial={productToSerialEntity(selectedProduct)}
          onClose={() => {
            setSelectedProduct(null);
            setShowEditProduct(false);
          }}
        />
      )}

      {showDeleteProduct && selectedProduct && (
        <DeleteProductDialog
          product={selectedProduct}
          orderId={order.ordernr}
          checklistId={checklist.id}
          onClose={() => {
            setSelectedProduct(null);
            setShowDeleteProduct(false);
          }}
        />
      )}

      {showMoveProduct && selectedProduct && (
        <MoveProductDialog
          product={selectedProduct}
          checklistId={checklist.id}
          order={order}
          onClose={() => {
            setSelectedProduct(null);
            setShowMoveProduct(false);
          }}
        />
      )}

      {showUninstallProduct && selectedProduct && (
        <UninstallProductDialog
          product={selectedProduct}
          checklistId={checklist.id}
          orderId={order.ordernr}
          onClose={() => {
            setSelectedProduct(null);
            setShowUninstallProduct(false);
          }}
        />
      )}
    </Section>
  );
};

export default ChecklistProductsSection;
